import React, { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Zap, ArrowLeft, Check } from 'lucide-react';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const { resetPassword } = useAuth();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) { setError('Passwords do not match'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setLoading(true);
    const result = await resetPassword(token, password);
    if (result.success) setDone(true);
    else setError(result.error);
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-8">
          <Zap className="w-6 h-6 text-[hsl(var(--accent))]" strokeWidth={2.5} />
          <span className="text-2xl font-bold tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>ZipAI</span>
        </div>
        {done ? (
          <div data-testid="reset-success">
            <div className="w-12 h-12 rounded-full bg-[hsl(var(--success))]/20 flex items-center justify-center mb-4">
              <Check className="w-6 h-6 text-[hsl(var(--success))]" />
            </div>
            <h1 className="text-3xl font-black tracking-tight mb-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Password reset</h1>
            <p className="text-muted-foreground mb-6">Your password has been updated. You can now sign in.</p>
            <Link to="/login"><Button className="rounded-[4px]" data-testid="go-to-login-btn">Go to login</Button></Link>
          </div>
        ) : (
          <>
            <h1 className="text-3xl font-black tracking-tight mb-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Set new password</h1>
            <p className="text-muted-foreground mb-8">Enter your new password below.</p>
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="reset-password-form">
              {error && <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 px-4 py-3 rounded-md">{error}</div>}
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">New Password</Label>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 6 characters" required data-testid="reset-password-input" className="h-11 rounded-[4px]" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">Confirm Password</Label>
                <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm password" required data-testid="reset-confirm-input" className="h-11 rounded-[4px]" />
              </div>
              <Button type="submit" disabled={loading} className="w-full h-11 rounded-[4px] font-semibold" data-testid="reset-submit-btn">
                {loading ? 'Resetting...' : 'Reset password'}
              </Button>
            </form>
            <p className="mt-6 text-sm text-muted-foreground">
              <Link to="/login" className="text-foreground font-medium hover:underline"><ArrowLeft className="w-3 h-3 inline mr-1" />Back to login</Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
